import {
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createSavedFilter, deleteSavedFilter } from 'src/api/mutations';
import { getSavedFilters } from 'src/api/queries';
import { ConfirmDialog } from 'src/components/common';
import { useStyledSnackbar } from 'src/components/common/styled-components/styledSnackbars';

/**
 * Renders a "Saved filters" dropdown + a "Save current" button for one
 * list page. Stores the page's filterGroups blob verbatim — loading a
 * preset is just re-applying it via `onLoad`.
 */
const SavedFiltersMenu = ({ pageKey, currentFilterGroups, hasActiveFilters, onLoad }) => {
  const queryClient = useQueryClient();
  const { successSnackbar, errorSnackbar } = useStyledSnackbar();
  const [anchor, setAnchor] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);

  const { data } = useQuery({
    queryKey: [getSavedFilters.key, pageKey],
    queryFn: () => getSavedFilters.func(pageKey),
    enabled: !!pageKey
  });
  const presets = data?.results || [];

  const createMutation = useMutation({
    mutationFn: createSavedFilter.func,
    mutationKey: [createSavedFilter.key],

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [getSavedFilters.key, pageKey]
      });
      successSnackbar('Filter preset saved.');
      setSaveOpen(false);
      setName('');
    },

    onError: (err) => errorSnackbar('Could not save preset.', err)
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSavedFilter.func,
    mutationKey: [deleteSavedFilter.key],

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [getSavedFilters.key, pageKey]
      });
      successSnackbar('Filter preset deleted.');
      setPendingDelete(null);
    },

    onError: (err) => {
      errorSnackbar('Could not delete preset.', err);
      setPendingDelete(null);
    }
  });

  const handleSave = () => {
    if (!name.trim() || !currentFilterGroups) return;
    createMutation.mutate({
      page_key: pageKey,
      name: name.trim(),
      filter_query: currentFilterGroups,
    });
  };

  return (
    <>
      <Tooltip title="Saved filter presets">
        <span>
          <IconButton
            onClick={(e) => setAnchor(e.currentTarget)}
            color={presets.length > 0 ? 'primary' : 'default'}
            aria-label="Saved filters"
          >
            <BookmarksIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 240 } }}
      >
        {presets.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No saved presets yet
            </Typography>
          </MenuItem>
        ) : (
          presets.map((p) => (
            <MenuItem
              key={p.id}
              onClick={() => {
                onLoad(p.filter_query);
                setAnchor(null);
              }}
            >
              <ListItemText primary={p.name} />
              <IconButton
                size="small"
                edge="end"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDelete(p);
                  setAnchor(null);
                }}
                aria-label={`Delete preset ${p.name}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem
          disabled={!hasActiveFilters}
          onClick={() => {
            setAnchor(null);
            setSaveOpen(true);
          }}
        >
          <ListItemIcon>
            <BookmarkAddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Save current filter…" />
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        onConfirm={handleSave}
        title="Save filter preset"
        body={
          <TextField
            autoFocus
            fullWidth
            label="Preset name"
            placeholder="e.g. My open incidents"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 80 }}
            sx={{ mt: 1 }}
          />
        }
        confirmLabel="Save"
        loading={createMutation.isPending}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => deleteMutation.mutate(pendingDelete.id)}
        title={`Delete preset "${pendingDelete?.name}"?`}
        body="This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
      />

      <Button
        variant="outlined"
        startIcon={<BookmarkAddIcon />}
        onClick={() => setSaveOpen(true)}
        disabled={!hasActiveFilters}
        sx={{ display: { xs: 'none', md: 'inline-flex' } }}
      >
        Save filter
      </Button>
    </>
  );
};

SavedFiltersMenu.propTypes = {
  pageKey: PropTypes.string.isRequired,
  currentFilterGroups: PropTypes.object,
  hasActiveFilters: PropTypes.bool,
  onLoad: PropTypes.func.isRequired,
};

export default SavedFiltersMenu;
